import Feature from 'ol/Feature';
import { fromCircle as PolygonFromCircle } from 'ol/geom/Polygon';
import LineString from 'ol/geom/LineString';
import { CIRCLE, LINE_STRING, POLYGON } from 'client/utils/constants/geometryType';
import OpenLayers from 'client/utils/helpers/OpenLayers/OpenLayers';

import { getValidDrawStyle, validFill, validStroke, validStrokeDashed } from 'client/pages/MapPage/layers/styles/validStyle';
import { getErrorDrawStyle, errorFill, errorStroke, errorStrokeDashed } from 'client/pages/MapPage/layers/styles/errorStyle';
import { LINE as DRAW_LINE } from 'client/utils/constants/symbolName';
import { createBufferedPolygonFromLine } from 'client/pages/MapPage/layers/styles/bufferedStyle';
import { addVertex } from 'client/pages/MapPage/layers/styles/vertexStyle';
import { addExtent } from 'client/pages/MapPage/layers/styles/extentStyle';
import FormattedMessageWithId from 'client/pages/MapPage/layers/styles/FormattedMessageWithId/FormattedMessageWithId';
import { Style } from 'ol/style';
import { lineHasAtLeastTwoDistinctPoints } from './utils';
import {
    getValidationPolicy,
    resolveValidation,
    resolveFallbackValidationFlow
} from 'client/pages/MapPage/layers/drawValidation';

import store from 'client/store/store';
import { setAlertOpen, setAlertProps } from 'client/store/alertSlice/alertSlice';
import { addDrawStatus, removeDrawStatus } from 'client/store/drawSlice/drawSlice';

import { drawStates } from 'client/utils/constants/appStates';
import { isFeatureWithinServiceArea } from 'client/utils/map/serviceAreaUtils';

const { VALID, INVALID, EXCEEDS_EXTENT, OUTSIDE_SERVICE_AREA } = drawStates;

const simpleLineStyleValid = new Style({ stroke: validStroke });
const simpleLineStyleError = new Style({ stroke: errorStroke });
const linePlacedSegmentStyleValid = new Style({ stroke: validStroke });
const lineMouseTailStyleError = new Style({ stroke: errorStroke });
const linePlacedSegmentBufferedStyleValid = new Style({ fill: validFill, stroke: validStrokeDashed });
const lineMouseTailBufferedStyleError = new Style({ fill: errorFill, stroke: errorStrokeDashed });

/**
 * Cached validity state for the *current* sketch feature (polygon/box mode only).
 *
 * OL draw interactions frequently call the style function with transient geometries.
 * In polygon/box mode we may get a Polygon (final intent) and also LineStrings
 * representing the current edges.
 *
 * Once we can evaluate the Polygon validity/extent, we apply that result to the sketch
 * LineStrings so the edges reflect self-intersection and extent errors consistently.
 */
let lastCompletePolygonIsValid;
let lastCompletePolygonIsGreaterThan;

let lastSketchFeature;

/**
 * Clears polygon-mode cache when OpenLayers starts a new sketch feature.
 */
const resetPolygonModeCacheIfNewSketchFeature = (feature) => {
    // New sketch feature => new drawing session. Clear cached polygon state so we don't
    // leak invalid/extent state into the next draw.
    if (feature !== lastSketchFeature) {
        lastSketchFeature = feature;
        lastCompletePolygonIsValid = undefined;
        lastCompletePolygonIsGreaterThan = undefined;
        // Notification dedupe must also reset per sketch feature so a new draw/modify
        // session can emit guidance and errors even when values match prior sessions.
        lastIsGreaterThan = null;
        lastIsValid = null;
        lastIsOutsideServiceArea = null;
        lastInfoMessageKey = null;
    }
};

const getStyleFromVariant = ({ styleVariant, geometryType, validDrawStyle, errorDrawStyle }) => {
    if (styleVariant === 'simple-line-valid') {
        return simpleLineStyleValid;
    }

    if (styleVariant === 'simple-line-error') {
        return errorDrawStyle[geometryType] || simpleLineStyleError;
    }

    if (styleVariant === 'default-valid') {
        return validDrawStyle[geometryType];
    }

    return errorDrawStyle[geometryType];
};

let lastIsGreaterThan;
let lastIsValid;
let lastIsOutsideServiceArea;
let lastInfoMessageKey;

/**
 * Tracks whether the polygon face was last computed as outside the service area.
 * Unlike lastCompletePolygonIsOutsideServiceArea, this is NOT reset when OL switches
 * between feature objects (e.g. sketchFeature_ vs sketchLine_) mid-session, so the
 * sketch edge can reliably inherit the polygon's service area state.
 */
let persistentPolygonIsOutsideServiceArea = null;

export const resetDrawStyleCache = () => {
    lastIsGreaterThan = null;
    lastIsValid = null;
    lastIsOutsideServiceArea = null;
    lastInfoMessageKey = null;
    persistentPolygonIsOutsideServiceArea = null;
};

const getBufferedGeometrySafe = (lineGeometry, bufferRadiusMetres) => {
    try {
        return createBufferedPolygonFromLine(lineGeometry, bufferRadiusMetres);
    } catch (e) {
        return null;
    }
};

const getInfoMessageKey = (descriptor) => {
    if (!descriptor?.id) {
        return null;
    }
    return `${descriptor.id}:${JSON.stringify(descriptor.values || {})}`;
};

export const updateInfoNotification = (infoMessageDescriptor) => {
    const key = getInfoMessageKey(infoMessageDescriptor);
    if (!key) {
        return;
    }

    if (lastInfoMessageKey === key) {
        return;
    }

    // Info guidance should not block a later re-entry into the same error state.
    lastIsGreaterThan = null;
    lastIsValid = null;
    lastInfoMessageKey = key;
    store.dispatch(setAlertProps({
        open: true,
        severity: 'info',
        content: <FormattedMessageWithId id={infoMessageDescriptor.id} values={infoMessageDescriptor.values} />
    }));
};

export const clearDrawErrorStatuses = () => {
    store.dispatch(removeDrawStatus(EXCEEDS_EXTENT));
    store.dispatch(removeDrawStatus(INVALID));
};

/**
 * Dispatches draw validity/extent alerts and draw status.
 *
 * This is called from inside an OL style function, so it must be cheap and stable.
 * We dedupe on `(isGreaterThan, isValid)` and ignore sketch geometry types we don’t
 * care about (e.g. Point) to prevent oscillation.
 */
export const updateNotifications = (geometryType, isGreaterThan, isValid, options = {}) => {
    const {
        extentMessageDescriptor = { id: 'mapPage.error.areaTooLarge', values: undefined },
        invalidMessageDescriptor = { id: 'mapPage.error.areaInvalid', values: undefined },
        isOutsideServiceArea = false
    } = options;

    // Only these geometry types drive draw validity/extent notifications.
    // OpenLayers interactions may call the style function for sketch Points too;
    // ignoring them prevents the dedupe key from oscillating Point<->LineString.
    if (geometryType !== CIRCLE && geometryType !== POLYGON && geometryType !== LINE_STRING) {
        return;
    }

    // Only dispatch when either validity or extent state changes.
    if (lastIsGreaterThan === isGreaterThan && lastIsValid === isValid && lastIsOutsideServiceArea === isOutsideServiceArea) {
        return;
    }
    lastIsGreaterThan = isGreaterThan;
    lastIsValid = isValid;
    lastIsOutsideServiceArea = isOutsideServiceArea;
    lastInfoMessageKey = null;

    if (isGreaterThan) {
        store.dispatch(setAlertProps({
            open: true,
            severity: 'error',
            content: <FormattedMessageWithId id={extentMessageDescriptor.id} values={extentMessageDescriptor.values} />
        }));
        store.dispatch(addDrawStatus(EXCEEDS_EXTENT));
        store.dispatch(removeDrawStatus(VALID));
    } else {
        store.dispatch(removeDrawStatus(EXCEEDS_EXTENT));
    }
    if (!isValid) {
        store.dispatch(setAlertProps({
            open: true,
            severity: 'error',
            content: <FormattedMessageWithId id={invalidMessageDescriptor.id} values={invalidMessageDescriptor.values} />
        }));
        store.dispatch(addDrawStatus(INVALID));
        store.dispatch(removeDrawStatus(VALID));
    } else {
        store.dispatch(addDrawStatus(VALID));
        store.dispatch(removeDrawStatus(INVALID));
    }

    if (isOutsideServiceArea) {
        store.dispatch(setAlertProps({
            open: true,
            severity: 'error',
            content: <FormattedMessageWithId id='mapPage.error.outsideServiceArea' />
        }));
        store.dispatch(addDrawStatus(OUTSIDE_SERVICE_AREA));
        store.dispatch(removeDrawStatus(VALID));
    } else {
        store.dispatch(removeDrawStatus(OUTSIDE_SERVICE_AREA));
    }

    if (!isGreaterThan && isValid && !isOutsideServiceArea) {
        store.dispatch(setAlertOpen(false));
    }
};

/**
 * Validates the feature geometry for “simplicity” (no self-intersections).
 */
export const isValidFeature = feature => {
    try {
        if (!feature || !(feature instanceof Feature)) {
            return false;
        }
        const geometry = feature.getGeometry();
        const geometryType = geometry?.getType?.();
        if (geometryType === CIRCLE) {
            feature = new Feature({
                geometry: PolygonFromCircle(geometry)
            });
        }
        if (geometryType === POLYGON) {
            const coords = geometry.getCoordinates();
            if (coords[0].length < 4) {
                return false;
            };
        }
        return OpenLayers.feature.isSimple(feature);
    } catch (e) {
        return false;
    }
};

/**
 * OpenLayers Draw interaction style function.
 *
 * Important behavior:
 * - In polygon/box mode, OL can style both Polygon + sketch LineStrings. We compute validity
 *   on the completed Polygon and apply it to the sketch edges.
 * - In polygon/box mode, sketch edges are rendered as a simple stroke when valid (to avoid
 *   the buffered “linear search” look) and as the existing multi-style error LineString
 *   styles when invalid (so the error is obvious).
 */
const styleFunction = (feature, userLimit, bufferRadiusMetres, drawType, lineWidthLimit) => {
    if (!feature || !(feature instanceof Feature)) {
        return new Error('style: invalid feature type');
    }

    resetPolygonModeCacheIfNewSketchFeature(feature);

    const styles = [];
    const geometry = feature?.getGeometry();
    if (geometry) {
        const geometryType = geometry.getType?.();
        const policy = getValidationPolicy(drawType);

        const evaluationContext = {
            drawType,
            geometryType,
            geometry,
            userLimit,
            feature
        };

        const usesPolygonSketchCache = Boolean(policy?.usesPolygonSketchCache);
        const isSketchEdgeGeometry = usesPolygonSketchCache && geometryType === LINE_STRING;
        const validationContext = {
            ...evaluationContext,
            isSketchEdgeGeometry
        };

        const resolvedValidation = policy
            ? resolveValidation(validationContext, policy)
            : resolveFallbackValidationFlow(geometryType, geometry, {
                isLineComplete: lineHasAtLeastTwoDistinctPoints
            });

        const shouldValidateGeometry = resolvedValidation.shouldValidateGeometry;
        const shouldValidateExtent = resolvedValidation.shouldValidateExtent;
        const shouldNotify = resolvedValidation.shouldNotify;
        const defaultExtentMessageDescriptor = resolvedValidation.extentMessageDescriptor;
        const stateNotification = resolvedValidation.stateNotification;
        const policyContext = resolvedValidation.policyContext || validationContext;

        const safeBufferRadiusMetres = Number.isFinite(bufferRadiusMetres) ? bufferRadiusMetres : 0;

        const isLineWidthExceeded =
            drawType &&
            geometryType === LINE_STRING &&
            Number.isFinite(lineWidthLimit) &&
            (safeBufferRadiusMetres * 2) > lineWidthLimit;

        const isGreaterThan = shouldValidateExtent
            ? (policy ? policy.getExtentExceeded(policyContext) : OpenLayers.geometry.isGreaterThan(geometry, userLimit))
            : false;

        const exceedsLimit = Boolean(isGreaterThan || isLineWidthExceeded);

        const extentMessageDescriptor = isLineWidthExceeded
            ? { id: 'mapPage.error.lineTooWide', values: { extent: lineWidthLimit } }
            : defaultExtentMessageDescriptor;

        if (!usesPolygonSketchCache) {
            lastCompletePolygonIsValid = undefined;
            lastCompletePolygonIsGreaterThan = undefined;
        }

        // Compute validity.
        // - In polygon/box mode, OL may render the same sketch as both Polygon and LineStrings.
        //   We compute validity on the completed Polygon, then apply that to the edges so they
        //   reflect self-intersection/extent errors too.
        let isValid;
        let effectiveIsGreaterThan = isGreaterThan;

        if (usesPolygonSketchCache && geometryType === POLYGON && shouldValidateGeometry) {
            isValid = isValidFeature(feature);
            lastCompletePolygonIsValid = isValid;
            lastCompletePolygonIsGreaterThan = isGreaterThan;
        } else if (usesPolygonSketchCache && geometryType === LINE_STRING) {
            if (typeof lastCompletePolygonIsValid === 'boolean') {
                isValid = lastCompletePolygonIsValid;
                effectiveIsGreaterThan = Boolean(lastCompletePolygonIsGreaterThan);
            } else {
                const drawStatus = store.getState?.()?.draw?.status;
                const hasGlobalInvalid = Array.isArray(drawStatus) && drawStatus.includes(INVALID);
                const hasGlobalExceeds = Array.isArray(drawStatus) && drawStatus.includes(EXCEEDS_EXTENT);

                // If sketch edges come in as a different feature instance (no cached polygon state),
                // inherit invalid/extent state from the draw status once it exists.
                if (hasGlobalInvalid) {
                    isValid = false;
                } else {
                    // Before we have a completed polygon (or any invalid state), keep edges looking “valid”.
                    isValid = true;
                }

                if (hasGlobalExceeds) {
                    effectiveIsGreaterThan = true;
                }
            }
        } else {
            isValid = shouldValidateGeometry ? isValidFeature(feature) : true;
        }

        const validDrawStyle = getValidDrawStyle(safeBufferRadiusMetres);
        const errorDrawStyle = getErrorDrawStyle(safeBufferRadiusMetres);

        // Service area containment check — runs before style computation so the
        // first point placed outside the service area is styled as an error immediately.
        // Applies to line mode and polygon/box modes.
        let isOutsideServiceArea = false;

        if (geometryType === LINE_STRING || geometryType === POLYGON) {
            const croppedServiceArea = store.getState().coverageAssetOwners.croppedServiceArea;

            if (croppedServiceArea && croppedServiceArea.type && croppedServiceArea.coordinates) {
                if (isSketchEdgeGeometry) {
                    // Sketch edge in polygon/box mode: check the edge independently
                    // and inherit the polygon's service area state.
                    // Use persistentPolygonIsOutsideServiceArea rather than
                    // lastCompletePolygonIsOutsideServiceArea because OL may call the
                    // style function with different feature objects for sketchFeature_ and
                    // sketchLine_, causing the per-feature cache to reset before the sketch
                    // edge can read it.
                    const edgeOutside = !isFeatureWithinServiceArea(feature, croppedServiceArea);
                    isOutsideServiceArea = edgeOutside || Boolean(persistentPolygonIsOutsideServiceArea);
                } else if (usesPolygonSketchCache && geometryType === POLYGON) {
                    // Polygon geometry in polygon/box mode: check and cache.
                    isOutsideServiceArea = !isFeatureWithinServiceArea(feature, croppedServiceArea);
                    // Also update the persistent cache so sketch edges can read it even
                    // when the feature reference differs from the polygon's feature.
                    persistentPolygonIsOutsideServiceArea = isOutsideServiceArea;
                } else {
                    // Line draw mode.
                    isOutsideServiceArea = !isFeatureWithinServiceArea(feature, croppedServiceArea);
                }

                // Dispatch service area status so finish condition and future
                // style calls reflect the current state.
                // Sketch edges (isSketchEdgeGeometry) must not remove OUTSIDE_SERVICE_AREA:
                // their geometry is only part of the polygon and does not represent the
                // authoritative containment check. Only the polygon face (or line draw mode)
                // can clear the status once the polygon is verified to be within the area.
                if (isOutsideServiceArea) {
                    store.dispatch(addDrawStatus(OUTSIDE_SERVICE_AREA));
                    store.dispatch(removeDrawStatus(VALID));
                } else if (!isSketchEdgeGeometry) {
                    store.dispatch(removeDrawStatus(OUTSIDE_SERVICE_AREA));
                }
            }
        }

        // Transient line tail handling for linear search: when the cursor goes out of bounds,
        // split the line into placed (green) and unplaced/tail (red) parts for visual feedback.
        const getTransientLineTailInvalidState = () => {
            if (drawType !== DRAW_LINE || geometryType !== LINE_STRING) {
                return null;
            }

            // Line width invalidation is not tied to the cursor tail.
            if (isLineWidthExceeded || (!isGreaterThan && isValid && !isOutsideServiceArea)) {
                return null;
            }

            const coordinates = geometry?.getCoordinates?.();
            if (!Array.isArray(coordinates) || coordinates.length < 3) {
                return null;
            }

            const placedCoordinates = coordinates.slice(0, -1);
            const mouseTailCoordinates = coordinates.slice(-2);
            const placedGeometry = new LineString(placedCoordinates);

            // If the placed path is already invalid/out of bounds, this is not a transient tail case.
            if (!lineHasAtLeastTwoDistinctPoints(placedGeometry)) {
                return null;
            }

            const placedIsGreaterThan = OpenLayers.geometry.isGreaterThan(placedGeometry, userLimit);
            const placedIsValid = isValidFeature(new Feature({ geometry: placedGeometry }));

            // Transient-tail rendering is only safe when the already placed path
            // is fully valid, including service-area containment.
            let placedIsOutsideServiceArea = false;
            const croppedServiceArea = store.getState().coverageAssetOwners.croppedServiceArea;
            if (croppedServiceArea && croppedServiceArea.type && croppedServiceArea.coordinates) {
                placedIsOutsideServiceArea = !isFeatureWithinServiceArea(
                    new Feature({ geometry: placedGeometry }),
                    croppedServiceArea
                );
            }

            if (placedIsGreaterThan || !placedIsValid || placedIsOutsideServiceArea) {
                return null;
            }

            return {
                placedGeometry,
                mouseTailGeometry: new LineString(mouseTailCoordinates),
                placedIsGreaterThan,
                placedIsValid,
                placedIsOutsideServiceArea
            };
        };

        const transientLineTailInvalidState = getTransientLineTailInvalidState();
        let notifyIsGreaterThan = isGreaterThan;
        let notifyIsValid = isValid;
        let notifyIsOutsideServiceArea = isOutsideServiceArea;

        if (transientLineTailInvalidState) {
            // Only suppress statuses when the corresponding error is introduced
            // by the transient cursor tail, never when the placed path is invalid.
            if (isGreaterThan && !transientLineTailInvalidState.placedIsGreaterThan) {
                notifyIsGreaterThan = false;
            }
            if (!isValid && transientLineTailInvalidState.placedIsValid) {
                notifyIsValid = true;
            }
            if (isOutsideServiceArea && !transientLineTailInvalidState.placedIsOutsideServiceArea) {
                notifyIsOutsideServiceArea = false;
            }
        }

        // In polygon/box mode, LineStrings represent edges.
        // - When the polygon is valid, keep edges simple (no buffered line-search look).
        // - When the polygon is invalid/exceeds extent, use the existing LineString error
        //   style (which can be a multi-style array) so the edges clearly show the error.
        const styleVariant = policy
            ? policy.getStyleVariant({
                ...policyContext,
                isGreaterThan: effectiveIsGreaterThan || isLineWidthExceeded || isOutsideServiceArea,
                isValid
            })
            : (!isGreaterThan && isValid && !isOutsideServiceArea ? 'default-valid' : 'default-error');

        let drawStyle;
        if (transientLineTailInvalidState) {
            const placedBufferedGeometry = getBufferedGeometrySafe(
                transientLineTailInvalidState.placedGeometry,
                safeBufferRadiusMetres
            );
            const mouseTailBufferedGeometry = getBufferedGeometrySafe(
                transientLineTailInvalidState.mouseTailGeometry,
                safeBufferRadiusMetres
            );

            linePlacedSegmentBufferedStyleValid.setGeometry(placedBufferedGeometry);
            linePlacedSegmentStyleValid.setGeometry(transientLineTailInvalidState.placedGeometry);
            lineMouseTailBufferedStyleError.setGeometry(mouseTailBufferedGeometry);
            lineMouseTailStyleError.setGeometry(transientLineTailInvalidState.mouseTailGeometry);
            drawStyle = [
                linePlacedSegmentBufferedStyleValid,
                linePlacedSegmentStyleValid,
                lineMouseTailBufferedStyleError,
                lineMouseTailStyleError
            ];
        } else {
            drawStyle = getStyleFromVariant({
                styleVariant,
                geometryType,
                validDrawStyle,
                errorDrawStyle
            });
        }

        if (!drawStyle) {
            return new Error('style: invalid geometry type');
        }

        const hasValidationError = Boolean(exceedsLimit || !isValid);

        if (stateNotification?.showInfo && stateNotification.infoMessageDescriptor) {
            if (policy?.shouldResetStatusesOnIncomplete?.(policyContext)) {
                clearDrawErrorStatuses();
            }
            if (!hasValidationError) {
                updateInfoNotification(stateNotification.infoMessageDescriptor);
            }
        } else {
            lastInfoMessageKey = null;
        }

        // Drive notifications only from the *intended* draw mode to avoid sketch geometry churn.
        // For transient line tail, use the placed geometry validity/extent for notifications.
        const notifyExceedsLimit = transientLineTailInvalidState
            ? notifyIsGreaterThan
            : exceedsLimit;
        if (shouldNotify && (hasValidationError || !stateNotification?.showInfo || isOutsideServiceArea)) {
            updateNotifications(geometryType, notifyExceedsLimit, notifyIsValid, {
                extentMessageDescriptor,
                isOutsideServiceArea: notifyIsOutsideServiceArea
            });
        }

        if (Array.isArray(drawStyle)) {
            styles.push(...drawStyle);
        } else {
            styles.push(drawStyle);
        }

        addVertex(geometry, styles, drawType === DRAW_LINE);
        if (geometryType !== LINE_STRING) {
            addExtent(geometry, styles);
        }
    }
    return styles;
};
export default styleFunction;
